import {
  CompetencyMapOptionsQuery,
  Examination_Type,
  ExaminationInfoQuery,
  useUpdateExaminationOpenDataMutation,
} from "@generated/graphql";
import { Box } from "@mui/material";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import Form from "components/form/Form";
import typography from "constants/typography.constants";
import { ADD_CANDIDATES_URL } from "constants/urls";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import useIsCreate from "hooks/useIsCreate";
import useUploadCandidates from "hooks/useUploadCandidates";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import InfoBlock from "../../components/InfoBlock";
import ResultAssessmentModeControlProps from "../../components/ResultAssessmentModeControl";
import {
  getCreateExaminationBaseData,
  getExaminationAvailableRangeFields,
  getExaminationResultAssessmentFields,
} from "../../helpers";
import useCreateExamination from "../../hooks/useCreateExamination";
import { ScenarioOption } from "../../types";
import useInitBaseExaminationForm from "../hooks/useInitExaminationForm";
import { GroupAndOpenInfoInputs, TrialsListRefetch } from "../types";
import GroupAndOpenLeft from "./Left";
import GroupAndOpenRight from "./Right";

interface ExaminationContentProps {
  examinationType: Examination_Type | undefined;
  data: ExaminationInfoQuery | undefined;
  trialsListRefetch: TrialsListRefetch;
  competencyMapsOptions: CompetencyMapOptionsQuery["competencyMaps"]["data"] | undefined;
  competencyMapsLoading: boolean;
  scenariosOptions: ScenarioOption[] | undefined;
  scenariosLoading: boolean;
}

const ExaminationContent = ({
  examinationType,
  data,
  trialsListRefetch,
  competencyMapsOptions,
  competencyMapsLoading,
  scenariosOptions,
  scenariosLoading,
}: ExaminationContentProps) => {
  const isCreate = useIsCreate();
  const [isEditing, setIsEditing] = useState<boolean>(isCreate);
  const { addToast } = useContext(ToastContext);
  const { createExaminationLoading, onCreateExamination } = useCreateExamination();
  const [updateExaminationOpenData] = useUpdateExaminationOpenDataMutation();

  const initFormData = useInitBaseExaminationForm(data?.examination, competencyMapsOptions, scenariosOptions);

  const form = useForm<GroupAndOpenInfoInputs>({ values: initFormData });
  const { handleSubmit, watch, reset, resetField } = form;
  const candidatesFile = watch("candidatesFile");

  const { uploadCandidates, uploadCandidatesLoading } = useUploadCandidates({
    uploadUrl: (fileId) => `${ADD_CANDIDATES_URL}/${fileId}`,
  });

  const handleUpdateExamination = async (formData: GroupAndOpenInfoInputs) => {
    const passingThreshold = Number(formData.passingThreshold);

    if (candidatesFile) {
      const res = await uploadCandidates(formData, () => resetField("candidatesFile"));
      if (res instanceof Response && res.ok) {
        trialsListRefetch();
      }
    }

    updateExaminationOpenData({
      variables: {
        input: {
          id: formData.id,
          ...(examinationType === Examination_Type.Group && {
            sendReminder: formData.sendReminder,
            ...(formData.sendReminder ? { reminderSchedule: formData.reminderSchedule } : {}),
          }),
          passingThreshold: Number.isNaN(passingThreshold) ? 40 : passingThreshold,
          ...getExaminationResultAssessmentFields(formData),
          ...getExaminationAvailableRangeFields(formData),
        },
      },
    })
      .then(() => {
        reset(form.getValues());
        addToast({ type: ToastTypeEnum.SUCCESS, text: "Тестирование успешно сохранено" });
        setIsEditing(false);
      })
      .catch(() => {
        addToast({ type: ToastTypeEnum.ERROR, text: "Ошибка при сохранении тестирования" });
      });
  };

  const onSubmit = (formData: GroupAndOpenInfoInputs) => {
    if (!isCreate) {
      handleUpdateExamination(formData);
      return;
    }

    const { competencyMap } = formData;
    if (competencyMap?.id || formData.scenario?.id) {
      onCreateExamination(
        {
          ...getCreateExaminationBaseData({
            ...formData,
            competencyMap,
          }),
          ...(examinationType === Examination_Type.Group && {
            sendReminder: formData.sendReminder,
            ...(formData.sendReminder ? { reminderSchedule: formData.reminderSchedule } : {}),
          }),
        },
        {
          middlewareAction:
            examinationType === Examination_Type.Group
              ? ({ id }) => uploadCandidates({ ...formData, examinationId: id })
              : undefined,
        }
      );
      return;
    }

    addToast({ type: ToastTypeEnum.ERROR, text: typography.errors.pleaseRepeat });
    // eslint-disable-next-line no-console
    console.error("onGroupSubmit: Не указан formData.competencyMap.id");
  };

  return (
    <Box sx={{ py: 2, px: 2.5, backgroundColor: "white", borderRadius: 2, width: "100%", marginBottom: 2.5 }}>
      <Form form={form}>
        <InfoBlock
          LeftElement={
            <GroupAndOpenLeft
              isCreate={isCreate}
              isEditing={isEditing}
            />
          }
          RightElement={
            <GroupAndOpenRight
              form={form}
              examinationType={examinationType}
              competencyMaps={competencyMapsOptions}
              competencyMapsLoading={competencyMapsLoading}
              scenarios={scenariosOptions}
              scenariosLoading={scenariosLoading}
              isEditing={isEditing}
            />
          }
          BottomElement={<ResultAssessmentModeControlProps isEditing={isEditing} />}
          ControlsElement={
            <SaveEditButtons
              isEdited={isEditing}
              onSubmit={handleSubmit(onSubmit)}
              setIsEdited={setIsEditing}
              loading={createExaminationLoading || uploadCandidatesLoading}
            />
          }
        />
      </Form>
    </Box>
  );
};

export default ExaminationContent;
